import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, MessagesSquare, Inbox, Star, Hash, ChevronLeft } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { usePerm, usePermScope } from '@/lib/perm';
import { useLang } from '@/lib/i18n';
import { api, errMsg } from '@/lib/api';
import { AppShell, EmptyState } from '@/components/AppShell';
import { ModuleIcon } from '@/components/ModuleIcon';
import { Button } from '@/components/ui/button';
import { ChannelSidebar } from '@/components/discuss/ChannelSidebar';
import { ChannelHeader } from '@/components/discuss/ChannelHeader';
import { MessageList } from '@/components/discuss/MessageList';
import { Composer } from '@/components/discuss/Composer';
import { NewChannelDialog, NewDmDialog } from '@/components/discuss/ChannelDialogs';

const POLL_MS = 4000;

const Thread = ({ company, channel, me, members, lang, isAdmin, onChanged, onLeft, onBack }) => {
  const [messages, setMessages] = useState(null);
  const [showPinned, setShowPinned] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const bottomRef = useRef(null);
  const scrollRef = useRef(null);
  const base = `/companies/${company.id}/discuss`;
  const id = lang === 'id';

  const scrollBottom = () => setTimeout(() => bottomRef.current?.scrollIntoView({ block: 'end' }), 30);

  useEffect(() => {
    setMessages(null); setShowPinned(false);
    api.get(`${base}/channels/${channel.id}/messages`, { params: { limit: 50 } }).then(({ data }) => { setMessages(data.items); setHasMore(data.has_more); scrollBottom(); }).catch((e) => toast.error(errMsg(e)));
    if (channel.is_member) api.post(`${base}/channels/${channel.id}/seen`).then(onChanged).catch(() => {});
  }, [channel.id]); // eslint-disable-line

  useEffect(() => {
    const h = setInterval(() => {
      if (!messages || showPinned) return;
      const last = messages[messages.length - 1]?.created_at;
      api.get(`${base}/channels/${channel.id}/messages`, { params: last ? { after: last } : { limit: 50 } }).then(({ data }) => {
        if (data.items.length) {
          setMessages((prev) => [...prev, ...data.items.filter((m) => !prev.find((p) => p.id === m.id))]);
          const el = scrollRef.current;
          if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 200) scrollBottom();
          if (channel.is_member) api.post(`${base}/channels/${channel.id}/seen`).then(onChanged).catch(() => {});
        }
      }).catch(() => {});
    }, POLL_MS);
    return () => clearInterval(h);
  }, [messages, channel.id, channel.is_member, showPinned]); // eslint-disable-line

  const loadMore = async () => {
    const first = messages[0]?.created_at;
    const { data } = await api.get(`${base}/channels/${channel.id}/messages`, { params: { before: first, limit: 50 } });
    setMessages((prev) => [...data.items, ...prev]); setHasMore(data.has_more);
  };
  const send = async (body, mention_user_ids, attachment_ids = []) => {
    try {
      const { data } = await api.post(`${base}/channels/${channel.id}/messages`, { body, mention_user_ids, attachment_ids });
      setMessages((prev) => [...(prev || []), data]); scrollBottom();
      if (!channel.is_member) onChanged();
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; }
  };
  const replace = (msg) => setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, ...msg } : m)));
  const onAction = async (kind, m, arg) => {
    try {
      if (kind === 'react') replace((await api.post(`${base}/messages/${m.id}/react`, { emoji: arg })).data);
      if (kind === 'star') { replace((await api.post(`${base}/messages/${m.id}/star`)).data); onChanged(); }
      if (kind === 'pin') { replace((await api.post(`${base}/messages/${m.id}/pin`)).data); toast.success(m.is_pinned ? (id ? 'Sematan dilepas' : 'Unpinned') : (id ? 'Pesan disematkan' : 'Message pinned')); }
      if (kind === 'edit') replace((await api.patch(`${base}/messages/${m.id}`, { body: arg })).data);
      if (kind === 'delete' && window.confirm(id ? 'Hapus pesan ini?' : 'Delete this message?')) { await api.delete(`${base}/messages/${m.id}`); setMessages((prev) => prev.filter((x) => x.id !== m.id)); }
    } catch (e) { toast.error(errMsg(e)); }
  };
  const visible = messages ? (showPinned ? messages.filter((m) => m.is_pinned) : messages) : null;
  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="discuss-thread">
      <ChannelHeader companyId={company.id} channel={channel} members={members} me={me} isAdmin={isAdmin} lang={lang} showPinned={showPinned} onTogglePinned={() => setShowPinned((v) => !v)} onChanged={onChanged} onLeft={onLeft} onBack={onBack} />
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        {!visible ? <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" /></div> : (
          <>
            {hasMore && !showPinned && <div className="flex justify-center py-3"><Button variant="outline" size="sm" className="rounded-xl text-xs" onClick={loadMore} data-testid="load-more-messages">{id ? 'Muat pesan sebelumnya' : 'Load earlier messages'}</Button></div>}
            {showPinned && <div className="mx-4 mt-3 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="pinned-banner">{id ? 'Menampilkan pesan yang disematkan.' : 'Showing pinned messages only.'}</div>}
            <MessageList messages={visible} me={me} lang={lang} onAction={onAction} isAdmin={isAdmin} emptyText={showPinned ? (id ? 'Belum ada pesan yang disematkan.' : 'No pinned messages yet.') : (id ? `Ini awal percakapan di ${channel.display_name}. Sapa tim Anda!` : `This is the start of ${channel.display_name}. Say hi to your team!`)} />
            <div ref={bottomRef} />
          </>
        )}
      </div>
      {!channel.is_member && channel.kind === 'channel' && <div className="border-t border-border bg-muted/40 px-4 py-2 text-xs text-muted-foreground" data-testid="not-member-hint">{id ? 'Anda belum bergabung. Kirim pesan untuk bergabung otomatis.' : 'You have not joined yet. Send a message to join automatically.'}</div>}
      <Composer members={members} me={me} onSend={send} lang={lang} channelId={channel.id} placeholder={id ? `Tulis pesan ke ${channel.kind === 'dm' ? channel.display_name : `#${channel.display_name}`}` : `Message ${channel.kind === 'dm' ? channel.display_name : `#${channel.display_name}`}`} />
    </div>
  );
};

const Mailbox = ({ company, box, me, lang, isAdmin, onChanged, onBack }) => {
  const [items, setItems] = useState(null);
  const base = `/companies/${company.id}/discuss`;
  const id = lang === 'id';
  const load = useCallback(() => api.get(`${base}/${box}`).then(({ data }) => setItems(data.items)).catch((e) => toast.error(errMsg(e))), [base, box]);
  useEffect(() => { setItems(null); load(); }, [load]);
  const onAction = async (kind, m, arg) => {
    try {
      if (kind === 'react') await api.post(`${base}/messages/${m.id}/react`, { emoji: arg });
      if (kind === 'star') await api.post(`${base}/messages/${m.id}/star`);
      if (kind === 'pin') await api.post(`${base}/messages/${m.id}/pin`);
      if (kind === 'edit') await api.patch(`${base}/messages/${m.id}`, { body: arg });
      if (kind === 'delete' && window.confirm(id ? 'Hapus pesan ini?' : 'Delete this message?')) await api.delete(`${base}/messages/${m.id}`);
      load(); onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const Icon = box === 'inbox' ? Inbox : Star;
  return (
    <div className="flex h-full min-h-0 flex-col" data-testid={`discuss-mailbox-${box}`}>
      <header className="flex items-center gap-3 border-b border-border bg-white px-4 py-2.5"><button type="button" onClick={onBack} className="md:hidden rounded-lg p-1 text-muted-foreground hover:bg-muted" aria-label="back"><ChevronLeft size={18} /></button><div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted text-muted-foreground"><Icon size={15} /></div><div><div className="font-display text-sm font-semibold">{box === 'inbox' ? (id ? 'Kotak masuk' : 'Inbox') : (id ? 'Berbintang' : 'Starred')}</div><div className="text-xs text-muted-foreground">{box === 'inbox' ? (id ? 'Pesan yang menyebut Anda' : 'Messages that mention you') : (id ? 'Pesan yang Anda tandai' : 'Messages you starred')}</div></div></header>
      <div className="flex-1 overflow-y-auto">
        {!items ? <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" /></div> : (
          <MessageList messages={items} me={me} lang={lang} onAction={onAction} isAdmin={isAdmin} showChannel emptyText={box === 'inbox' ? (id ? 'Belum ada yang menyebut Anda. Gunakan @nama di kanal untuk menyebut rekan.' : 'Nobody has mentioned you yet. Use @name in a channel to mention teammates.') : (id ? 'Tandai pesan penting dengan ikon bintang untuk menemukannya di sini.' : 'Star important messages to find them here.')} />
        )}
      </div>
    </div>
  );
};

export default function DiscussPage() {
  const { company, user } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const { box, channelId } = useParams();
  const [data, setData] = useState(null);
  const [members, setMembers] = useState([]);
  const [newChannel, setNewChannel] = useState(false);
  const [newDm, setNewDm] = useState(false);
  const id = lang === 'id';
  const enabled = company?.modules?.find((m) => m.key === 'discuss')?.enabled;
  const { canManage: isAdmin } = usePerm('discuss');
  usePermScope('discuss');
  const base = company ? `/companies/${company.id}/discuss` : '';

  const loadChannels = useCallback(() => { if (company && enabled) return api.get(`${base}/channels`).then(({ data }) => { setData(data); return data; }).catch((e) => toast.error(errMsg(e))); }, [company, enabled, base]);
  useEffect(() => { loadChannels(); if (company && enabled) api.get(`/companies/${company.id}/members`).then(({ data }) => setMembers(data.members)).catch(() => {}); }, [loadChannels, company, enabled]);
  useEffect(() => { const h = setInterval(loadChannels, 10000); return () => clearInterval(h); }, [loadChannels]);
  useEffect(() => {
    if (!data || box) return;
    const first = data.channels.find((c) => c.kind === 'channel') || data.channels[0];
    navigate(first ? `/discuss/channel/${first.id}` : '/discuss/inbox', { replace: true });
  }, [data, box, navigate]);

  const go = (b, cid) => navigate(cid ? `/discuss/${b}/${cid}` : `/discuss/${b}`);
  const openDm = async (uid) => { try { const { data: ch } = await api.post(`${base}/dm`, { user_id: uid }); setNewDm(false); await loadChannels(); go('channel', ch.id); } catch (e) { toast.error(errMsg(e)); } };

  if (!company) return <AppShell><div className="flex justify-center py-24"><Loader2 className="animate-spin text-primary" /></div></AppShell>;
  if (!enabled) return <AppShell moduleName={id ? 'Diskusi' : 'Discuss'} moduleIcon={<ModuleIcon moduleKey="discuss" size="sm" />}><div className="mx-auto max-w-xl px-4 py-16"><EmptyState icon={MessagesSquare} title={id ? 'Modul Diskusi belum diinstal' : 'Discuss module not installed'} description={id ? 'Instal modul Diskusi melalui menu Aplikasi untuk mulai berkomunikasi dengan tim.' : 'Install the Discuss module from the Apps menu to start chatting with your team.'} action={<Button className="rounded-xl" onClick={() => navigate('/modules')} data-testid="discuss-go-modules">{id ? 'Buka Aplikasi' : 'Open Apps'}</Button>} /></div></AppShell>;

  const channel = box === 'channel' ? data?.channels.find((c) => c.id === channelId) : null;
  const showMain = box === 'inbox' || box === 'starred' || (box === 'channel' && !!channelId);
  const back = () => navigate('/discuss/channel');
  return (
    <AppShell fullBleed moduleName={id ? 'Diskusi' : 'Discuss'} moduleIcon={<ModuleIcon moduleKey="discuss" size="sm" />}>
      <div className="flex flex-1 min-h-0">
        <aside className={`${showMain ? 'hidden md:flex' : 'flex'} w-full md:w-64 shrink-0 flex-col border-r border-border bg-white/70`}>
          {!data ? <div className="flex justify-center py-10"><Loader2 className="animate-spin text-primary" /></div> : <ChannelSidebar data={data} box={box} channelId={channelId} go={go} onNewChannel={() => setNewChannel(true)} onNewDm={() => setNewDm(true)} lang={lang} />}
        </aside>
        <main className={`${showMain ? 'flex' : 'hidden md:flex'} flex-1 min-w-0 flex-col bg-[#fbfbfd]`}>
          {!data ? <div className="flex flex-1 items-center justify-center"><Loader2 className="animate-spin text-primary" /></div>
            : (box === 'inbox' || box === 'starred') ? <Mailbox company={company} box={box} me={user} lang={lang} isAdmin={isAdmin} onChanged={loadChannels} onBack={back} />
              : channel ? <Thread key={channel.id} company={company} channel={channel} me={user} members={members} lang={lang} isAdmin={isAdmin} onChanged={loadChannels} onLeft={() => { loadChannels(); navigate('/discuss'); }} onBack={back} />
                : <div className="flex flex-1 items-center justify-center p-10"><EmptyState icon={Hash} title={id ? 'Pilih kanal' : 'Pick a channel'} description={id ? 'Pilih kanal atau pesan langsung di sebelah kiri untuk mulai berdiskusi.' : 'Choose a channel or direct message on the left to start chatting.'} /></div>}
        </main>
      </div>
      <NewChannelDialog companyId={company.id} open={newChannel} onClose={() => setNewChannel(false)} members={members} me={user} lang={lang} onCreated={async (ch) => { setNewChannel(false); await loadChannels(); go('channel', ch.id); }} />
      <NewDmDialog open={newDm} onClose={() => setNewDm(false)} members={members} me={user} lang={lang} onPick={openDm} />
    </AppShell>
  );
}
